import type { Dictionary } from './de';

/**
 * English interface strings.
 *
 * Kept structurally identical to the German dictionary so TypeScript flags any
 * key that is added on one side but missing on the other.
 */
export const en: Dictionary = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    create: 'Create',
    close: 'Close',
    search: 'Search …',
    loading: 'Loading …',
    none: 'none',
    notConfigured: 'not configured',
    never: 'never',
    yes: 'Yes',
    no: 'No',
    active: 'active',
    inactive: 'inactive',
    total: 'total',
    of: 'of',
    perMonth: '/mo',
    included: 'included',
    select: 'select',
    copy: 'Copy',
    copied: 'Copied',
    download: 'Download',
    back: 'Back',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    optional: 'optional',
    required: 'required',
    error: 'Error',
    retry: 'Try again',
  },

  brand: {
    tagline: 'WireGuard management',
  },

  nav: {
    section: 'Management',
    operations: 'Operations',
    dashboard: 'Overview',
    instances: 'Instances',
    plans: 'Plans',
    account: 'Account',
    admin: 'Administration',
    adminServers: 'Servers',
    adminIpam: 'IP management',
    adminInstances: 'Instances',
    billing: 'Balance',
    invoices: 'Invoices',
    adminUsers: 'Users',
    adminBilling: 'Balances',
    adminPlans: 'Plans',
    adminMail: 'Emails',
    adminSettings: 'Settings',
    adminAudit: 'Audit log',
    logout: 'Sign out',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    toggleSidebar: 'Collapse or expand sidebar',
    accountSecurity: 'Account and security',
    quickSearch: 'Search pages or instances …',
    quickHintNav: 'Arrow keys to navigate',
    quickHintOpen: 'Enter to open',
    quickHintClose: 'Esc to close',
    noResults: 'No matches.',
    groupNavigation: 'Navigation',
    groupInstances: 'Instances',
    language: 'Language',
    themeToLight: 'Switch to light appearance',
    themeToDark: 'Switch to dark appearance',
  },

  live: {
    live: 'Live',
    connecting: 'Connecting',
    offline: 'Disconnected',
    hint: 'State of the live connection to the server',
    peersOnline: '{count} peers online',
    peerOnline: '{count} peer online',
  },

  auth: {
    signInTitle: 'Sign in to manage your instances.',
    signUpTitle: 'Create an account and book your first plan.',
    email: 'Email address',
    password: 'Password',
    name: 'Name',
    nameHint: 'Optional, shown in your account.',
    passwordHint: 'At least 10 characters.',
    signIn: 'Sign in',
    signUp: 'Create account',
    noAccount: 'No account yet?',
    hasAccount: 'Already registered?',
    toSignUp: 'Sign up now',
    toSignIn: 'Back to sign in',
    totpCode: 'Two-factor code',
    totpHint: 'Code from your authenticator app or a recovery code.',
    secured: 'Connection and session are secured end to end.',
    failed: 'Sign-in failed',
    signedOut: 'You have been signed out.',

    forgotPassword: 'Forgot your password?',
    forgotTitle: 'Reset your password',
    forgotSubtitle: 'We will send a link to your email address.',
    forgotSubmit: 'Request link',
    forgotSent:
      'If an account exists for this address, the link is on its way. Please check your spam folder too.',
    resetTitle: 'Set a new password',
    resetSubtitle: 'All existing sessions will be signed out.',
    resetSubmit: 'Save password',
    resetSuccess: 'Password saved. You are now signed in.',
    resetInvalid: 'This link is invalid or has expired. Please request a new one.',
    newPassword: 'New password',
    repeatPassword: 'Repeat password',
    passwordMismatch: 'The passwords do not match.',
    passwordTooShort: 'At least 10 characters.',
    backToLogin: 'Back to sign in',

    verifyTitle: 'Confirm your email address',
    verifyPending: 'Confirming your address …',
    verifySuccess: 'Address confirmed. Welcome!',
    verifyInvalid: 'This confirmation link is invalid or has expired.',
    verifyBannerTitle: 'Email address not confirmed yet',
    verifyBannerText: 'Confirm your address to be able to order instances.',
    verifyResend: 'Send link again',
    verifyResent: 'Confirmation link sent.',
    verifyAlready: 'Your address is already confirmed.',
    registerVerifySent:
      'Account created! We sent you a confirmation link - please check your inbox (and spam folder). After clicking you are signed in automatically.',
  },

  dashboard: {
    title: 'Overview',
    subtitle: 'All booked WireGuard instances and their current load.',
    orderPlan: 'Book a plan',
    instances: 'Instances',
    activeCount: '{count} active',
    peersOnline: 'Peers online',
    inbound: 'Inbound (client upload)',
    outbound: 'Outbound (client download)',
    emptyTitle: 'No instance booked yet',
    emptyText:
      'Pick a plan – the WireGuard instance is set up within seconds and ready to use right away.',
    emptyAction: 'View plans',
    uplinkLoad: 'Uplink utilisation',
    peerSlots: 'Peer slots used',
    syncedAgo: 'Synced {time}',
  },

  instances: {
    title: 'Instances',
    subtitle: 'Every instance runs on its own WireGuard interface on the server.',
    searchPlaceholder: 'Search by name, interface or network',
    searchLabel: 'Search instances',
    columnInstance: 'Instance',
    columnPlan: 'Plan',
    columnPeers: 'Peers',
    columnRate: 'Data rate',
    columnLoad: 'Utilisation',
    columnSync: 'Synced',
    noMatch: 'No instance matches your search.',
    emptyTitle: 'No instance booked yet',
    emptyText: 'Book a plan to set up your first WireGuard instance.',
    backToList: 'All instances',
    notFound: 'Instance not found',
    notFoundText: 'The instance does not exist or belongs to another account.',
    dedicated: 'Dedicated IPv4',
    shared: 'Shared NAT',
    inbound: 'Inbound',
    outbound: 'Outbound',
    uplink: 'Uplink',
    peers: 'Peers',
    delete: 'Delete instance',
    deleteConfirm:
      'Instance “{name}” will be shut down along with all {count} peers. All connections end immediately and no further charges apply. Deleted by accident? The operator can fully restore the instance within 72 hours – after that it is gone for good.',
    deleteFinal: 'Delete instance',
    deleted: 'Instance deleted.',
    tabPeers: 'Peers',
    tabTraffic: 'Traffic',
    tabSettings: 'Settings',
    trafficTitle: 'Last 24 hours',
    trafficSubtitle: 'Green: client upload, blue: client download',
    utilization: 'Utilisation',
    utilizationOfLimit: '{percent}% of plan limit',
    totalReceived: 'Total received',
    totalSent: 'Total sent',
    planLimit: 'Plan limit',
    settingsGeneral: 'General',
    settingsGeneralHint: 'Name, DNS and MTU of the instance',
    label: 'Label',
    dnsServers: 'DNS servers for clients',
    mtu: 'MTU',
    mtuHint: '1420 suits most connections. Lower values help on PPPoE links.',
    siteToSiteNets: 'Allowed site-to-site networks',
    siteToSiteHint: 'Only these private networks may be reached through the tunnel.',
    settingsSaved: 'Settings saved.',
    uplinkHint: 'Changes take effect immediately, existing connections stay up.',
    uplinkChanged: 'Uplink changed to {value}.',
    uplinkConfirmTitle: 'Change uplink?',
    uplinkConfirmText:
      'The uplink switches to {bandwidth} immediately – existing connections are preserved.',
    uplinkConfirmAction: 'Change with costs',
    uplinkPriceCurrent: 'Current monthly price',
    uplinkPriceNew: 'New monthly price',
    uplinkPriceHint: 'The new price applies from the next charge. There is no pro-rata billing.',
    includedInPlan: 'included in plan',
    surcharge: '+{price} per month',
    connectionData: 'Connection details',
    endpoint: 'Endpoint',
    serverPublicKey: 'Server public key',
    ipv4Overlay: 'Internal IPv4 network',
    ipv6Prefix: 'IPv6 prefix',
    outgoingIpv4: 'Outgoing IPv4',
    dedicatedSuffix: '(dedicated)',
    sharedNat: 'shared (NAT)',
    plan: 'Plan',
    lastSync: 'Last sync',
    notAssigned: 'not assigned',
  },

  peers: {
    title: 'Peers',
    slotsUsed: '{used} of {max} slots used',
    add: 'Add peer',
    emptyTitle: 'No peers yet',
    emptyText: 'Add your first peer – keys, IP address and QR code are generated automatically.',
    columnAddresses: 'Addresses',
    columnHandshake: 'Last handshake',
    columnTraffic: 'Traffic',
    inSuffix: 'in',
    outSuffix: 'out',
    online: 'Online',
    offline: 'Offline',
    disabled: 'Disabled',
    deviceName: 'Device name',
    devicePlaceholder: 'Anna’s laptop',
    keys: 'Keys',
    keysServer: 'Generate on server',
    keysServerHint: 'Configuration and QR code are shown once',
    keysOwn: 'Bring your own public key',
    keysOwnHint: 'Private key never leaves the device',
    publicKey: 'Public key',
    publicKeyPlaceholder: 'base64-encoded X25519 key',
    clientAllowedIps: 'Client AllowedIPs',
    clientAllowedIpsHint:
      '0.0.0.0/0 routes all traffic through the tunnel. For split tunnelling list individual networks only.',
    routing: 'Routing',
    routingFull: 'All traffic',
    routingFullHint: 'Everything goes through the tunnel',
    routingTunnel: 'Tunnel network only',
    routingTunnelHint: 'Split tunnel: only this instance’s networks',
    routingCustom: 'Custom',
    routingCustomHint: 'Define the networks yourself',

    reopenConfig: 'View configuration',
    configWithoutKey: 'Without private key',
    configWithoutKeyText:
      'The private key is never stored and is therefore missing here. Fill it in if you still have it — otherwise create a fresh pair via “Generate new key”.',
    behindPeer: 'Networks behind this peer',
    behindPeerHint: 'Site-to-site, comma separated',
    usePsk: 'Use a pre-shared key (extra protection against quantum attacks)',
    created: 'Peer “{name}” created.',
    deleteConfirm:
      'Remove “{name}” permanently? The device loses access immediately; all other connections stay up.',
    removed: 'Peer “{name}” removed.',
    enabled: '{name} enabled.',
    disabledToast: '{name} disabled.',
    limitReached: 'The plan’s peer limit is reached ({max} peers).',
    showConfig: 'Configuration and QR code',
    rotate: 'Generate new key',
    rotateTitle: 'Generate a new key?',
    rotateText:
      'A new key pair will be created for “{name}”. The previous configuration becomes invalid immediately and must be replaced on the device.',
    rotateConfirm: 'Rotate key',
    rotated: 'New key generated – please transfer the configuration.',
    toggleEnable: 'Enable',
    toggleDisable: 'Disable',
  },

  config: {
    title: 'Configuration: {name}',
    onceOnlyTitle: 'This configuration is shown only once',
    onceOnlyText:
      'The private key is delivered here and nowhere stored. Save the file now – afterwards only a new key can be generated.',
    qrTab: 'QR code',
    fileTab: 'Configuration file',
    scanHint: 'In the WireGuard app choose “Add from QR code” and scan this code.',
    copyText: 'Copy text',
    downloadConf: 'Download .conf',
    copyFailed: 'Copying failed. Please select the text manually.',
    noStoredKey: 'No configuration available for this peer.',
    noStoredKeyText:
      'The private key was never stored. Generate a new key to receive a fresh configuration.',
  },

  plans: {
    title: 'Plans & pricing',
    subtitle: 'Cancel monthly, upgrade any time, DDoS protection from the first minute.',
    popular: 'Popular',
    uplink: 'uplink',
    clientsPerServer: '{count} clients per server',
    dedicatedIpv4Included: '1 dedicated IPv4 included',
    sharedIpv4: 'Shared IPv4 (dedicated from +€2.50)',
    ipv6Included: 'Own IPv6 /64 included',
    ddos: 'DDoS protection included',
    unlimited: 'Unlimited traffic (fair use)',
    siteToSite: 'Site-to-site routing included',
    splitTunnel: 'Split tunnel & kill switch',
    prioritySupport: 'Priority network support',
    networkUpgrade: 'Network upgrade',
    from: 'from',
    order: 'Order',
    footnote:
      'The uplink can be upgraded in every plan – the surcharge is monthly and can be changed again at any time.',
    orderTitle: 'Order {plan}',
    orderSubtitle: 'The instance is provisioned immediately and ready to use afterwards.',
    instanceName: 'Label',
    instanceNameHint: 'Freely chosen, e.g. the site or the team.',
    addons: 'Add-ons',
    dedicatedIpv4Addon: 'Dedicated IPv4 address',
    dedicatedIpv4AddonHint: 'Exclusive public address instead of shared NAT',
    basePrice: 'Base price',
    monthly: 'Monthly',
    orderConfirm: 'Place binding order',
    ordered: '{name} has been set up ({ifName}).',
    orderFailed: 'Order failed',
  },

  account: {
    title: 'Account',
    subtitle: 'Credentials and two-factor authentication.',
    profile: 'Profile',
    role: 'Role',
    roleAdmin: 'Administrator',
    roleCustomer: 'Customer',
    memberSince: 'Member since',
    changePassword: 'Change password',
    changePasswordHint: 'All other sessions will be signed out afterwards.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordChanged: 'Password changed. Other sessions have been signed out.',
    twoFactor: 'Two-factor authentication',
    twoFactorHint: 'Extra protection with an authenticator app (TOTP).',
    twoFactorActive: 'Active',
    twoFactorInactive: 'Not set up',
    twoFactorActiveHint: 'A code is requested at every sign-in.',
    twoFactorInactiveHint: 'Recommended for accounts with production instances.',
    setUp: 'Set up',
    disable: 'Disable',
    setupTitle: 'Set up two-factor authentication',
    setupIntro:
      'You need an authenticator app such as Aegis, 1Password or Google Authenticator. In the next step you receive a secret to store there.',
    next: 'Continue',
    secret: 'Secret',
    secretCopied: 'Secret copied.',
    codeFromApp: 'Code from the app',
    activate: 'Activate',
    recoveryIntro:
      'Keep these recovery codes safe. Each code works exactly once and replaces the code from the app.',
    done: 'Done',
    disableTitle: 'Disable two-factor authentication',
    disableText: 'Confirm with your password. Afterwards the password alone is enough to sign in.',
    disabled: 'Two-factor authentication disabled.',
  },

  admin: {
    title: 'Administration',
    subtitle: 'Servers, IP management, customer instances and audit log.',
    reconcile: 'Reconcile system state',
    reconciled: 'Reconciliation finished: {restored} restored, {failed} failed.',
    servers: 'Servers',
    customers: 'Customers',
    dedicatedIpv4: 'Dedicated IPv4',
    freeCount: '{count} free',
    inPool: '{count} in pool',
    publicIp: 'Public IPv4',
    wanInterface: 'Uplink interface',
    internalNet: 'Internal network',
    portRange: 'Port range',
    ifIndexRange: 'Interface indices',
    capacity: 'Capacity',
    noServer: 'No server configured yet. The installer creates it during setup.',
    ipv4Pools: 'IPv4 pools',
    ipv6Pools: 'IPv6 prefixes',
    strategyDescending: 'descending from the highest address',
    strategyAscending: 'ascending',
    ipamTitle: 'IP management',
    ipamSubtitle: 'Public IPv4 addresses are handed out descending – highest free address first.',
    ipamAvailable: 'Free',
    ipamAssigned: 'Assigned',
    ipamReserved: 'Reserved',
    ipamNextAssignment: 'Next assignment',
    ipamGateway: 'Gateway',
    ipamNoPools: 'No public IPv4 pool configured for this server yet.',
    ipamReserve: 'Reserve',
    ipamRelease: 'Release',
    ipamReserved_toast: 'Address reserved.',
    ipamReleased: 'Address released.',
    ipamAssignedTo: 'Assigned to {instance}',
    ipv6Allocations: 'Assigned prefixes',
    ipv6NoAllocations: 'No prefixes assigned yet.',
    customer: 'Customer',
    net: 'Network',
    suspend: 'Suspend',
    resume: 'Resume',
    suspended: '{name} has been suspended.',
    resumed: '{name} has been resumed.',
    editUser: 'Edit',
    editUserTitle: 'Edit account – {email}',
    userSaved: 'Account {email} saved.',
    roleSelfHint: 'You cannot change your own role.',
    emailVerifiedLabel: 'Email address counts as confirmed',
    newPassword: 'Set new password',
    newPasswordHint: 'Leave empty to keep it. Signs out all sessions of this account.',
    sendVerificationTitle: 'Confirmation link',
    sendVerificationHint: 'If the mail never arrived. Creates a fresh link; older ones expire.',
    sendVerification: 'Send link',
    sendVerificationDone: 'Confirmation link sent to {to}.',
    totpResetTitle: 'Two-factor authentication',
    totpResetHint: 'For customers who lost their device. Signs out all sessions.',
    totpReset: 'Reset 2FA',
    totpResetDone: 'Two-factor authentication of {email} was reset.',
    deleteUserTitle: 'Delete account',
    deleteUserHint: 'Irrevocable – balance, transactions and invoices are removed too. Type {email} to confirm.',
    deleteUserBlocked: 'Not possible: the account still has {count} instance(s). Delete them or empty the trash first.',
    userDeleted: 'Account {email} has been deleted.',
    restore: 'Restore',
    restored: '{name} has been restored – the customer was notified.',
    purge: 'Delete permanently',
    purgeTitle: 'Delete permanently?',
    purgeText:
      '“{name}” will be removed immediately and irrevocably. Peers, keys and the reserved IP addresses are released – restoring is no longer possible afterwards.',
    purged: '{name} has been permanently deleted.',
    trashUntil: 'gone for good on {date}',
    users: 'Users',
    createUser: 'Create user',
    userCreated: 'User created.',
    initialPassword: 'Initial password',
    twoFa: '2FA',
    lastLogin: 'Last sign-in',
    state: 'State',
    locked: 'locked',
    activate: 'Activate',
    deactivate: 'Deactivate',
    userToggled: '{email} has been {state}.',
    activated: 'activated',
    deactivated: 'deactivated',
    auditTitle: 'Audit log',
    auditSubtitle: 'The most recent security-relevant actions',
    auditFilter: 'Filter by action, account or IP',
    auditEmpty: 'No entries yet',
    auditEmptyText: 'As soon as actions are performed they appear here.',
    auditNoMatch: 'No matches',
    auditNoMatchText: 'Adjust the filter.',
    anonymous: 'anonymous',
  },

  billing: {
    title: 'Balance',
    subtitle: 'Account balance, top-ups and transactions.',
    balance: 'Balance',
    available: 'Available',
    creditLimit: 'Credit limit',
    monthlyTotal: 'Due monthly',
    monthlyHint: 'Total across all running instances.',
    availableHint: 'Balance plus credit limit.',
    noCreditLimit: 'No credit limit granted.',
    coversMonths: 'Covers roughly {months} months.',
    coversLessThanMonth: 'Not enough for the next month.',

    topUp: 'Top up balance',
    topUpWithPayPal: 'Top up with PayPal',
    topUpAmount: 'Amount',
    topUpRange: 'Between {min} and {max}.',
    topUpUnavailable: 'Top-ups are not configured on this server.',
    topUpUnavailableHint: 'Contact the operator to add balance.',
    topUpRedirect: 'Redirecting you to PayPal …',
    topUpSuccess: '{amount} has been credited.',
    topUpCancelled: 'The top-up was cancelled.',
    topUpAlreadyCredited: 'This payment was already credited.',

    lowBalanceWarning: 'Your balance no longer covers the next charge.',
    negativeBalance: 'Your account is in the negative. Please top up.',
    suspendedWarning:
      'At least one instance is suspended for lack of funds. It will be deleted once the grace period ends.',

    history: 'Transactions',
    historyEmpty: 'No transactions yet',
    historyEmptyText: 'Top-ups and charges will appear here.',
    date: 'Date',
    description: 'Description',
    amount: 'Amount',
    balanceAfter: 'Balance',

    typeTOPUP: 'Top-up',
    typeCHARGE: 'Charge',
    typeREFUND: 'Refund',
    typeADJUSTMENT: 'Adjustment',
    sourceMANUAL: 'manual',
    sourcePAYPAL: 'PayPal',
    sourceSYSTEM: 'automatic',

    invoicesTitle: 'Invoices',
    invoicesSubtitle: 'Invoices issued for this account.',
    invoicesEmpty: 'No invoices',
    invoicesEmptyText: 'Once a billing run completes, invoices appear here.',
    invoiceNumber: 'Number',
    invoicePeriod: 'Period',
    invoiceDue: 'Due',
    invoiceNet: 'Net',
    invoiceTax: 'VAT ({rate}%)',
    invoiceGross: 'Total',
    invoiceOpen: 'Open',
    invoicePaid: 'Paid',
    invoiceCancelled: 'Cancelled',
    invoiceDraft: 'Draft',
    invoiceItems: 'Items',
    invoiceRecipient: 'Bill to',
    backToInvoices: 'Back to overview',

    addressTitle: 'Billing address',
    addressSubtitle: 'Used on your invoices.',
    company: 'Company',
    street: 'Street and number',
    zip: 'Postcode',
    city: 'City',
    country: 'Country',
    vatId: 'VAT ID',
    addressSaved: 'Address saved.',

    modePREPAID: 'Prepaid',
    modeINVOICE: 'Invoice',
    modeLabel: 'Billing',
    invoiceModeHint: 'Your instances are invoiced monthly instead of charged to your balance.',

    adminTitle: 'Balances and billing',
    adminSubtitle: 'Credit accounts, set credit limits and manage invoices.',
    selectUser: 'Select account',
    adjustTitle: 'Create transaction',
    adjustAmount: 'Amount (negative debits the account)',
    adjustNote: 'Reason',
    adjustNoteHint: 'Recorded with the transaction and visible to the customer.',
    adjustBooked: 'Transaction created.',
    limitsTitle: 'Limits',
    limitsSaved: 'Limits saved.',
    lowBalanceThreshold: 'Warning threshold',
    lowBalanceHint: 'A warning email is sent at this available amount. -1 disables it.',
    markPaid: 'Mark as paid',
    issueInvoice: 'Issue now',
    invoiceIssued: 'Invoice {number} issued.',
    invoiceMarkedPaid: 'Invoice marked as paid.',
    allUsers: 'All accounts',
  },

  plansAdmin: {
    title: 'Plans',
    frozenHint: 'Price changes only affect new orders – running instances keep their frozen price.',
    create: 'Create plan',
    editTitle: 'Edit plan – {name}',
    plan: 'Plan',
    price: 'Price',
    priceField: 'Monthly price (€)',
    limits: 'Limits',
    peersCount: '{count} peers',
    features: 'Features',
    tierCount: '{count} uplink tiers',
    name: 'Name',
    slugHint: 'Unique, lowercase letters, digits and dashes only. Do not change after creation.',
    description: 'Description',
    baseBandwidth: 'Uplink (Mbit/s)',
    sortOrder: 'Sort order',
    ipMode: 'Outgoing IPv4',
    isPublic: 'Visible in the plan catalogue',
    tiers: 'Bookable uplink tiers',
    tiersHint: 'Surcharges on the base price. Customers can switch between tiers at any time.',
    addTier: 'Add tier',
    hiddenBadge: 'hidden',
    hide: 'Remove from catalogue',
    show: 'Show in catalogue',
    hidden: '“{name}” is now hidden – existing customers continue unchanged.',
    shown: '“{name}” is now visible in the catalogue.',
    saved: 'Plan saved.',
    deleted: 'Plan “{name}” has been deleted.',
    deleteTitle: 'Delete plan?',
    deleteText: '“{name}” will be removed. Only possible while no instance uses it – otherwise hide it instead.',
  },

  settingsAdmin: {
    title: 'Settings',
    subtitle: 'Panel values override the .env. Resetting restores the server value.',
    saved: 'Settings saved – effective immediately, no restart needed.',
    saveChanges: 'Save {count} change(s)',
    overridden: 'Panel',
    willReset: 'will be reset',
    reset: 'Reset to .env value',
    envIs: 'From .env: {value}',
    envEmpty: 'Not set in .env.',
    envSet: 'Set in .env.',
    secretPlaceholder: 'Enter new secret',
    testMail: 'Send test mail',
    mailOk: 'Test mail sent to {to} – please check your inbox.',
    mailFailed: 'Sending failed – details in the service log.',
    testPaypal: 'Check credentials',
    paypalOk: 'PayPal credentials valid ({mode}).',
    paypalFailed: 'PayPal check failed: {message}',

    group_smtp: 'Email delivery',
    group_smtp_hint: 'SMTP access and sender. Port 587 = STARTTLS (secure off), 465 = TLS (secure on).',
    group_paypal: 'PayPal',
    group_paypal_hint: 'Credentials from the developer portal. Without client ID and secret, top-ups are off.',
    group_whmcs: 'WHMCS integration',
    group_whmcs_hint:
      'Bridge to WHMCS: ordering, payment and cancellation are handled by WHMCS, FluxPanel provisions. The token here must match the one in the WHMCS module.',
    group_registration: 'Registration',
    group_registration_hint: 'Who may sign up, and when ordering becomes possible.',
    group_billing: 'Billing and grace periods',
    group_billing_hint: 'Amounts in cents, tax rate in permille (190 = 19%).',

    k_smtp_host: 'SMTP host',
    k_smtp_port: 'Port',
    k_smtp_secure: 'Implicit TLS (port 465 only)',
    k_smtp_user: 'Username',
    k_smtp_password: 'Password',
    k_smtp_allowInsecureTls: 'Allow self-signed certificates',
    k_mail_from: 'Sender address',
    k_mail_fromName: 'Sender name',
    k_mail_replyTo: 'Reply-to address (optional)',
    k_mail_bcc: 'Blind copy to the operator (optional)',
    k_mail_publicUrl: 'Link base of mails (panel URL)',

    k_paypal_mode: 'Environment',
    k_paypal_clientId: 'Client ID',
    k_paypal_clientSecret: 'Client secret',
    k_paypal_webhookId: 'Webhook ID',
    k_paypal_minTopupCents: 'Minimum top-up (cents)',
    k_paypal_maxTopupCents: 'Maximum top-up (cents)',

    k_whmcs_apiToken: 'API token (shared secret)',
    k_whmcs_suppressMails: 'No system mails for WHMCS instances (WHMCS sends its own)',
    k_registration_open: 'Self-registration allowed',
    k_registration_requireVerifiedEmail: 'Ordering requires confirmed email',

    k_billing_taxRatePermille: 'Tax rate (permille)',
    k_billing_defaultCreditLimitCents: 'Default credit limit for new accounts (cents)',
    k_billing_lowBalanceCents: 'Low-balance warning threshold (cents, -1 = off)',
    k_billing_suspendGraceDays: 'Suspension until deletion (days)',
    k_billing_deleteRetentionHours: 'Trash retention (hours)',
    k_billing_invoiceDueDays: 'Invoice due period (days)',
  },

  mailAdmin: {
    title: 'Email templates',
    subtitle: 'Adjust subject and text of every system mail – per language, with placeholders.',
    template: 'Template',
    subject: 'Subject',
    body: 'Text',
    bodyHint: 'Separate paragraphs with blank lines. Placeholders in curly braces are replaced on send.',
    placeholders: 'Available placeholders',
    editHint: 'Empty or unchanged fields fall back to the default text.',
    fixedHint:
      'Greeting, info boxes, buttons and links are fixed and always keep working – only the body text is overridden.',
    overridden: 'customised',
    saved: 'Template saved.',
    reset: 'Reset to default',
    resetDone: 'Template reset to the default text.',
    sendTest: 'Send test to me',
    testSent: 'Test mail sent to {to}.',
    smtpMissing:
      'Mail delivery is not configured (SMTP_HOST missing). Templates can be edited but not tested.',

    kind_passwordReset: 'Password reset',
    kind_passwordChanged: 'Password changed',
    kind_emailVerify: 'Confirm address',
    kind_lowBalance: 'Balance running low',
    kind_topupReceived: 'Balance topped up',
    kind_charged: 'Monthly charge',
    kind_instanceCreated: 'Instance ready',
    kind_instanceSuspended: 'Instance paused',
    kind_instanceResumed: 'Instance back up',
    kind_instanceDeleted: 'Instance deleted (customer)',
    kind_instanceExpired: 'Instance permanently removed',
    kind_peerDeleted: 'Device removed',
    kind_invoiceIssued: 'Invoice issued',

    logTitle: 'Delivery log',
    logHint: 'Every attempt with status - without contents; links do not belong in the database.',
    logAll: 'All',
    logSent: 'Sent',
    logFailed: 'Failed',
    logDisabled: 'Delivery off',
    logEmpty: 'No mails sent yet',
  },

  errors: {
    generic: 'Action failed',
    loadFailed: 'Could not load data',
    saveFailed: 'Saving failed',
    deleteFailed: 'Deleting failed',
    insufficientFunds: 'Your balance does not cover this order.',
    insufficientFundsHint: '{missing} short. Top up your balance and order again.',
    emailNotVerified: 'Please confirm your email address first.',
  },
};
